import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock } from "lucide-react";
import CTABlock from "@/components/ui/CTABlock";
import FAQAccordion from "@/components/ui/FAQAccordion";
import { getPost } from "@/data/blogPosts";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const fmtDate = (iso) =>
    new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export default function BlogPost() {
    const { slug } = useParams();
    const post = getPost(slug);
    useDocumentTitle(
        post ? post.metaTitle : "Screen Care Guides · A1 Screening",
        post ? post.metaDescription : ""
    );
    if (!post) return <Navigate to="/blog" replace />;

    const base = `/images/gallery/web/${post.heroPhoto}`;
    const related = (post.related || []).map(getPost).filter(Boolean);

    return (
        <div data-testid="blog-post-page">
            <div className="a1-bg-alt">
                <div className="a1-container pt-8 pb-10 max-w-4xl">
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-1.5 text-sm no-underline mb-5 hover:text-[var(--color-accent)]"
                        style={{ color: "var(--color-text-muted)" }}
                        data-testid="blog-back-link"
                    >
                        <ArrowLeft size={14} /> All guides
                    </Link>
                    <p className="a1-eyebrow mb-3">{post.category}</p>
                    <h1 className="mb-4" data-testid="blog-post-title">{post.title}</h1>
                    <p className="text-sm flex items-center gap-2 m-0" style={{ color: "var(--color-text-muted)" }}>
                        A1 Screening · Naples, FL · {fmtDate(post.date)} · <Clock size={13} /> {post.readTime} min read
                    </p>
                </div>
            </div>

            <article className="a1-container max-w-4xl py-10">
                <div className="relative aspect-[16/7] overflow-hidden rounded-lg mb-10" style={{ background: "var(--color-bg-alt)" }}>
                    <picture>
                        <source type="image/webp" srcSet={`${base}-md.webp 800w, ${base}-lg.webp 1600w`} sizes="(min-width: 1024px) 896px, 100vw" />
                        <img
                            src={`${base}-lg.jpg`}
                            alt="A1 Screening pool cage and screen enclosure project photo"
                            loading="eager"
                            decoding="async"
                            className="absolute inset-0 w-full h-full object-cover object-center"
                        />
                    </picture>
                </div>

                <div className="max-w-3xl">
                    {post.sections.map((s, i) => (
                        <section key={i} className="mb-9" data-testid={`blog-section-${i}`}>
                            <h2 className="mb-4">{s.heading}</h2>
                            {(s.paragraphs || []).map((p, j) => (
                                <p key={j} className="leading-relaxed mb-4" style={{ color: "var(--color-text-muted)" }}>{p}</p>
                            ))}
                            {s.list ? (
                                <ul className="grid gap-2.5 pl-0 list-none">
                                    {s.list.map((item, j) => (
                                        <li key={j} className="flex items-start gap-2.5 text-[15px] leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                                            <CheckCircle2 size={17} className="shrink-0 mt-1 text-[var(--color-accent)]" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </section>
                    ))}

                    {post.faq?.length ? (
                        <section className="mb-9" data-testid="blog-post-faq">
                            <h2 className="mb-4">Quick answers</h2>
                            <FAQAccordion
                                items={post.faq}
                                prefix={`blogfaq-${post.slug}`}
                                defaultOpen={-1}
                            />
                        </section>
                    ) : null}

                    <p className="text-xs mt-10" style={{ color: "var(--color-text-dim)" }}>
                        This guide is general information for Southwest Florida homeowners. Every enclosure is different — recommendations and pricing for your pool cage are confirmed after technician review.
                    </p>
                </div>

                {related.length ? (
                    <section className="mt-12" data-testid="blog-related">
                        <h2 className="mb-5">Keep reading</h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            {related.map((r) => (
                                <Link
                                    key={r.slug}
                                    to={`/blog/${r.slug}`}
                                    className="a1-card a1-card-link no-underline flex flex-col gap-1.5"
                                    data-testid={`blog-related-${r.slug}`}
                                >
                                    <span className="a1-eyebrow">{r.category}</span>
                                    <span className="font-bold leading-snug" style={{ color: "var(--color-text)" }}>{r.title}</span>
                                    <span className="text-xs" style={{ color: "var(--color-text-dim)" }}>{r.readTime} min read</span>
                                </Link>
                            ))}
                        </div>
                    </section>
                ) : null}
            </article>

            <CTABlock
                headline="Ready to have a technician take a look?"
                body="Estimates are free and there's no obligation. Call us or send your details and photos — we'll take it from there."
            />
        </div>
    );
}
